import React, { memo } from 'react'
import { SubscriptionPlans } from '@pff-consumer/schema'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import { getCouponInfo } from '@pff-consumer/utils/lib/accounts/get-coupon-info'
import { CheckoutSummaryProps } from './checkout-summary.props'
import { useStyles } from './checkout-summary.styles'
import { Text, TextDescriptionVariant } from '@pff-consumer/tweed-ui/atoms/text'
import { colors } from '@pff-consumer/core-ui'

const { usePromoCode, useGetIsAccountsPage } = checkoutHooks

export const CheckoutSummary = memo((props: CheckoutSummaryProps) => {
  const { selectedPlan, price, isDiscountApplied, showKitRow } = props
  const coupon = usePromoCode()
  const isAccountsPage = useGetIsAccountsPage()
  const styles = useStyles()
  const isAnnualPlanSelected = selectedPlan === SubscriptionPlans.PREMIUM_ANNUAL
  const currency = price.currency.symbol
  const discount = price.now.discount
  const planDescription = isAnnualPlanSelected ? 'PFF+ Annual' : 'PFF+ Monthly'
  const originalPrice = isAnnualPlanSelected ? price.now.plan : price.now.plan

  const { total: totalPrice, plan: currentPrice } = price.now
  const formattedCurrentPrice = `${currency}${currentPrice}`
  const formattedTotalPrice = `${currency}${totalPrice}`
  const formattedSetupFee = price.now.setup_fee !== '0.00' ? `${currency}${price.now.setup_fee}` : 'FREE'
  const discountPercentage = `- ${Math.round((Number(discount) / Number(originalPrice)) * 100)}%`
  const showDiscountInfo = isDiscountApplied && originalPrice !== totalPrice
  const { discountLabel } = getCouponInfo(coupon)

  const renderDescription = (testId: string, content: string) => {
    return (
      <Text
        testID={`checkout.summary.${testId}`}
        variant={TextDescriptionVariant.TEXT_BASE_REGULAR}
        textAlign='center'
      >
        {content}
      </Text>
    )
  }

  const renderPrice = (testId: string, content: string) => {
    return (
      <Text
        testID={`checkout.summary.${testId}`}
        variant={TextDescriptionVariant.TEXT_BASE_BOLD}
      >
        {content}
      </Text>
    )
  }

  return (
    <section className={styles.container}>
      {isAccountsPage ? null : (
        <div className={styles.checkoutSummaryRow}>
          {renderDescription('planDescription', planDescription)}
          {renderPrice('currentPrice', formattedCurrentPrice)}
        </div>
      )}
      {showKitRow ? (
        <div className={styles.checkoutSummaryRow}>
          {renderDescription('draftKitFee', 'Season Pass')}
          {renderPrice('draftKitSetupFee', formattedSetupFee)}
        </div>
      ) : null}
      {showDiscountInfo ? (
        <div className={styles.checkoutSummaryRow}>
          <Text
            testID='checkout.summary.discountCoupon'
            variant={TextDescriptionVariant.TEXT_BASE_REGULAR}
            textAlign='center'
          >
            Discount:
            <span className={styles.coupon}>
              <Text
                colorOverride={colors.webInverseTextColor}
                testID='checkout.summary.couponCode'
                variant={TextDescriptionVariant.TEXT_2XS_REGULAR}
                textAlign='center'
              >
                {discountLabel}
              </Text>
            </span>
          </Text>
          {renderPrice('discountPercentage', discountPercentage)}
        </div>
      ) : null}
      <div className={`${styles.checkoutSummaryRow} ${styles.finalPriceContainer}`}>
        {renderDescription('total', 'Total')}
        {renderPrice('totalPrice', formattedTotalPrice)}
      </div>
    </section>
  )
})
